import { useCallback, useMemo, useState } from "react";
import { useFormik } from "formik";
import _ from "lodash";
import * as yup from "yup";

import {
  CloseIcon,
  EsignDealGraphIcon,
  EsignDocGraphIcon,
  ForwardToInboxOutlinedIcon,
  PeopleAltOutlinedIcon,
  PersonOutlineOutlinedIcon,
  SupervisorAccountIcon,
} from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { emailValidate } from "utils/common";
import { handleInvalidRequest } from "utils/sdk";

import Autocomplete from "components/Autocomplete";
import Box from "components/Box";
import Button from "components/Button";
import Drawer from "components/Drawer";
import IconButton from "components/IconButton";
import SidepanelHeader from "components/SidepanelHeader";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Tag from "components/Tag";
import Text from "components/Text";
import TextField from "components/TextField";
import Tooltip from "components/Tooltip";
import UserAvatar from "components/UserAvatar";

import MemoESignatureEmailAssigneeBox from "entities/Memo/components/Sections/MemoESignatureDrawer/components/MemoESignatureEmailAssigneeBox";
import MemoESignatureUserAssigneeBox from "entities/Memo/components/Sections/MemoESignatureDrawer/components/MemoESignatureUserAssigneeBox";
import { getOrdinalSuffix } from "entities/Memo/components/Sections/MemoESignatureDrawer/utils";
import {
  IMemoSignatureRoleWithAssignees,
  IMemoSignatureUserData,
  memoSignatureRoleAssigneeCreateOrUpdate,
  useCompanyUserList,
  useUserAssociatedWithDealList,
} from "entities/Memo/sdk";
import { useMemoSignatureRoleWithPrefetchedAssigneesList } from "entities/Memo/sdk";

const VALIDATION_SCHEMA = yup.object({
  email: yup
    .string()
    .default(null)
    .nullable()
    .test("custom-email-validation", "Must be a valid email!", (value) => {
      if (value === "") {
        return false;
      }
      if (_.isNil(value)) {
        return true;
      }
      return emailValidate({ email: value.trim() });
    }),

  userId: yup.number().default(null).nullable(),
});

const initialValues = {
  email: null,
  userId: null,
};

export interface IESignAssigneeModificationTable {
  dealId?: number;
  memoId?: number;
}

const ESignAssigneeModificationTable = ({
  dealId,
  memoId,
}: IESignAssigneeModificationTable) => {
  const [modificationSidebarData, setModificationSidebarData] =
    useState<null | { role: IMemoSignatureRoleWithAssignees }>(null);
  const [isEmailAssignmentEnabled, setIsEmailAssignmentEnabled] =
    useState<boolean>(false);

  const {
    isLoading,
    data: roles,
    mutate: mutateRoles,
  } = useMemoSignatureRoleWithPrefetchedAssigneesList({
    dealId,
    memoId,
  });

  const { data: users } = useUserAssociatedWithDealList({
    dealId: dealId || 0,
    enabled: !_.isNil(modificationSidebarData) && !_.isNil(dealId),
  });

  const { data: companyUsers } = useCompanyUserList({
    enabled: !_.isNil(modificationSidebarData) && _.isNil(dealId),
  });

  const handleFormSubmit = useCallback(
    (values: { email: string | null; userId: number | null }, formik) => {
      if (!_.isNil(modificationSidebarData?.role.id)) {
        return memoSignatureRoleAssigneeCreateOrUpdate({
          memoSignatureAssigneeId: _.get(
            modificationSidebarData,
            "role.assignees.0.id"
          ),
          roleId: modificationSidebarData.role.id,
          userId: values.userId || undefined,
          email: !_.isNil(values.email) ? values.email : undefined,
          dealId,
          memoId,
        })
          .then(() => {
            setModificationSidebarData(null);
            setIsEmailAssignmentEnabled(false);
            mutateRoles();
            formik.resetForm();
          })
          .catch(handleInvalidRequest);
      }
    },
    [modificationSidebarData, dealId, memoId, mutateRoles]
  );

  const formik = useFormik({
    initialValues: { ...initialValues },
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: handleFormSubmit,
  });

  const handleClose = useCallback(() => {
    formik.resetForm();
    setModificationSidebarData(null);
    setIsEmailAssignmentEnabled(false);
  }, [formik]);

  const userOptions = useMemo(
    () =>
      !_.isNil(dealId)
        ? users?.map((user) => ({
            id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
            avatar: user.avatar,
            email: user.email,
            company_name: user.company_name,
          })) || []
        : companyUsers?.map((user) => ({
            id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
            avatar: user.avatar,
            email: user.email,
            company_name: user.company_name,
          })) || [],
    [users, companyUsers, dealId]
  );

  const selectedUserOption = useMemo(
    () => userOptions.find((user) => user.id === formik.values.userId) || null,
    [formik.values.userId, userOptions]
  );

  const emailOnChangeHandle = useCallback(
    ({ value }: { value: string }) => {
      formik.setFieldValue("email", value);
      formik.setFieldValue("userId", null);
    },
    [formik]
  );

  const userOnChangeHandle = useCallback(
    (event, value: IMemoSignatureUserData | null | string) => {
      if (!_.isNil(value) && !_.isString(value)) {
        if (value.id !== 0) {
          formik.setFieldValue("userId", value.id);
          setIsEmailAssignmentEnabled(false);
          formik.setFieldValue("email", null);
        } else {
          setIsEmailAssignmentEnabled(true);
          formik.setFieldValue("userId", null);
          formik.setFieldValue("email", value.email);
        }
      } else {
        formik.setFieldValue("user", null);
      }
    },
    [formik]
  );

  const disableAssignContactButton = useMemo(
    () =>
      (_.isNil(formik.values.email) ||
        _.isEmpty(formik.values.email) ||
        !isEmailAssignmentEnabled) &&
      _.isNil(selectedUserOption),
    [formik, selectedUserOption, isEmailAssignmentEnabled]
  );

  const getRoleAssignmentLevelTag = useCallback(
    ({ role }: { role: IMemoSignatureRoleWithAssignees }) => {
      const assignmentLevel = _.get(role, "assignees.0.assignment_level", null);

      if (_.isNil(assignmentLevel)) {
        return null;
      }

      if (assignmentLevel === "company" && !_.isNil(dealId)) {
        return <Tag label="Company level role" variant="blue20" />;
      }

      if (assignmentLevel === "deal" && !_.isNil(memoId)) {
        return <Tag label="Deal level role" variant="blue20" />;
      }

      return null;
    },
    [dealId, memoId]
  );

  const onAssigneeDelete = useCallback(
    ({ roleId, assigneeId }: { roleId: number; assigneeId: number }) =>
      memoSignatureRoleAssigneeCreateOrUpdate({
        memoSignatureAssigneeId: assigneeId,
        roleId,
        dealId,
        memoId,
      })
        .then(() => {
          mutateRoles();
        })
        .catch(handleInvalidRequest),
    [dealId, memoId, mutateRoles]
  );

  const drawerHeaderData = useMemo(() => {
    if (_.isNil(dealId) && _.isNil(memoId)) {
      return {
        title: "You are assigning this contact at the company level.",
        description:
          "If this role is filled by the same contact across all deals and their documents you can assign them here. They will be automatically populated in all documents that include this role for all of your deals. If needed, you can re-assign the contact on the deal or document level.",
        extraInfoText:
          "Please note: no invitations will be sent to the email until a document signing process has been initiated.",
      };
    }

    if (_.isNil(memoId)) {
      return {
        title: "You are assigning this contact at the deal level.",
        description:
          "If this role is filled by the same contact across all documents in the deal, you can assign them here. They will be automatically populated in all documents that include this role. If needed, you can re-assign the contact on the document level.",
        icon: <EsignDealGraphIcon style={{ paddingRight: "16px" }} />,
        extraInfoText:
          "Please note: no invitations will be sent to the email until a document signing process has been initiated.",
      };
    }

    return {
      title: "You are assigning this contact at the document level.",
      description:
        "If this contact will need to sign multiple documents in this deal, they can be assigned at the deal level.",
      icon: <EsignDocGraphIcon style={{ paddingRight: "16px" }} />,
    };
  }, [dealId, memoId]);

  const isMemoLevel = useMemo(
    () => !_.isNil(memoId) && !_.isNil(dealId),
    [memoId, dealId]
  );

  return (
    <>
      <Stack
        direction="row"
        borderBottom={`1px solid ${colors.blue60}`}
        paddingBottom={0.5}
        gap={2}
      >
        {!_.isNil(memoId) && !_.isNil(dealId) && (
          <Text variant="labelSmall" flexBasis="40px">
            order
          </Text>
        )}
        <Text variant="labelSmall" flexBasis="160px">
          Role
        </Text>
        <Text variant="labelSmall">Contact assigned</Text>
      </Stack>
      {isLoading &&
        _.range(4).map(() => (
          <Stack
            direction="row"
            borderBottom={`1px solid ${colors.blue60}`}
            paddingBottom={1.5}
            marginTop={1}
            gap={isMemoLevel ? 2 : 1}
          >
            {isMemoLevel && (
              <Stack gap={1} flexBasis="40px">
                <Skeleton height={14} width={40} variant="rectangular" />
              </Stack>
            )}
            <Stack gap={1} flexBasis="210px">
              <Skeleton height={14} width={40} variant="rectangular" />
            </Stack>

            <Skeleton
              height={66}
              width="100%"
              variant="rectangular"
              sx={{ borderRadius: "8px" }}
            />
          </Stack>
        ))}

      {!isLoading && roles?.length === 0 && (
        <Text variant="text3" color={colors.gray80} marginTop={2}>
          No Memo e-sign roles have been added yet. Please contact our Customer
          Success team to have them set up.
        </Text>
      )}

      {!isLoading &&
        roles?.map((role) => {
          const order = getOrdinalSuffix({ number: role.order });
          return (
            <Stack
              direction="row"
              borderBottom={`1px solid ${colors.blue60}`}
              paddingBottom={1.5}
              marginTop={1}
              gap={2}
            >
              {isMemoLevel && (
                <Tooltip title={order}>
                  <Stack
                    flexBasis="40px"
                    alignItems="flex-start"
                    height="fit-content"
                  >
                    <Tag
                      label={order}
                      variant="gray60"
                      sx={{
                        padding: theme.spacing(0.25, 1),
                        maxWidth: "40px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    />
                  </Stack>
                </Tooltip>
              )}
              <Tooltip title={role.name} placement="bottom-start">
                <Stack
                  gap={1}
                  flexBasis="160px"
                  alignItems="flex-start"
                  height="fit-content"
                >
                  <Tag
                    label={role.name}
                    variant="gray100"
                    sx={{
                      padding: theme.spacing(0.25, 1),
                      maxWidth: "160px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  />
                  {getRoleAssignmentLevelTag({ role })}
                </Stack>
              </Tooltip>
              <Stack sx={{ flexGrow: 1 }}>
                {role.assignees.length === 0 && (
                  <Stack gap={1} alignItems="center" flexDirection="row">
                    <PersonOutlineOutlinedIcon sx={{ color: colors.red60 }} />
                    <Text
                      variant="text3"
                      color={colors.red60}
                      fontWeight="bold"
                    >
                      No contact assigned yet.
                    </Text>
                  </Stack>
                )}

                {role.assignees.map((assignee) =>
                  !_.isNil(assignee.user) ? (
                    <MemoESignatureUserAssigneeBox
                      key={assignee.user?.id}
                      user={assignee.user}
                      onReplace={() => setModificationSidebarData({ role })}
                      onDelete={() =>
                        onAssigneeDelete({
                          roleId: role.id,
                          assigneeId: assignee.id,
                        })
                      }
                      disableDelete={
                        !_.isNil(getRoleAssignmentLevelTag({ role }))
                      }
                    />
                  ) : (
                    <MemoESignatureEmailAssigneeBox
                      key={assignee.email}
                      email={assignee.email as string}
                      onReplace={() => setModificationSidebarData({ role })}
                      onDelete={() =>
                        onAssigneeDelete({
                          roleId: role.id,
                          assigneeId: assignee.id,
                        })
                      }
                      disableDelete={
                        !_.isNil(getRoleAssignmentLevelTag({ role }))
                      }
                    />
                  )
                )}
              </Stack>
              {role.assignees.length === 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PeopleAltOutlinedIcon />}
                  sx={{ flexBasis: "150px", color: colors.black }}
                  onClick={() => setModificationSidebarData({ role })}
                >
                  Assign contact
                </Button>
              )}
            </Stack>
          );
        })}

      <Drawer
        open={!_.isNil(modificationSidebarData)}
        anchor="right"
        sx={{
          width: "800px",
          "& .MuiDrawer-paper": {
            width: "800px",
            display: "flex",
          },
        }}
        onClose={handleClose}
      >
        <SidepanelHeader>
          <Tooltip
            title={modificationSidebarData?.role?.name}
            placement="bottom-start"
          >
            <Stack alignItems="flex-start">
              <Stack direction="row" gap={1.5} alignItems="center">
                <SupervisorAccountIcon
                  sx={{
                    width: "34px",
                    height: "34px",
                  }}
                />
                <Text variant="h1">Assign contact to role</Text>
              </Stack>
              <Tag
                label={modificationSidebarData?.role?.name || ""}
                variant="gray100"
                sx={{
                  fontSize: "16px",
                  maxWidth: "160px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  height: "18px",
                }}
              />
            </Stack>
          </Tooltip>

          <IconButton
            size="small"
            onClick={handleClose}
            sx={{ alignSelf: "flex-start" }}
          >
            <CloseIcon />
          </IconButton>
        </SidepanelHeader>

        <Stack paddingX={4} paddingY={2} flexGrow={1}>
          <Stack
            gap={0.25}
            padding={2}
            sx={{
              borderRadius: "8px",
              backgroundColor: colors.blue20,
              display: "flex",
              flexDirection: _.isNil(drawerHeaderData.icon) ? "column" : "row",
            }}
          >
            {drawerHeaderData.icon}
            <Stack>
              <Text variant="text2" fontWeight="bold">
                {drawerHeaderData.title}
              </Text>

              <Text variant="text4" color={colors.gray80}>
                {drawerHeaderData.description}
              </Text>
            </Stack>
          </Stack>

          {isEmailAssignmentEnabled && (
            <Stack
              direction="row"
              gap={1}
              marginTop={2}
              alignItems="flex-start"
              sx={{
                padding: theme.spacing(1.5, 1.5),
                backgroundColor: colors.gray10,
                borderRadius: "8px",
              }}
            >
              <ForwardToInboxOutlinedIcon
                sx={{ width: "24px", height: "24px" }}
              />
              <Stack>
                <Text variant="text2" fontWeight={500}>
                  E-sign request & invite to create Builders Patch account
                </Text>
                <Text variant="text4" color={colors.gray80}>
                  You are inviting this person to sign your document via
                  Builders Patch. They will need to register an account with us
                  if they don't have one already. Once they log into Builders
                  Patch they will be able to review and sign the document. They
                  will not have access to your deals, checklists, or any other
                  documents you have not shared with them. If you wish to share
                  any additional resources with them you will have to do that
                  separately.
                </Text>
                {!_.isNil(drawerHeaderData.extraInfoText) && (
                  <Text variant="text4" color={colors.gray80} paddingTop={2}>
                    {drawerHeaderData.extraInfoText}
                  </Text>
                )}
              </Stack>
            </Stack>
          )}

          <Text variant="text3" fontWeight={500} marginTop={3} marginBottom={1}>
            {isEmailAssignmentEnabled
              ? "Email address:"
              : "Search existing contacts:"}
          </Text>

          <Autocomplete
            freeSolo
            selectOnFocus
            value={selectedUserOption}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select from the list..."
                helperText={formik.touched.email && formik.errors.email}
                error={formik.touched.email && !_.isNil(formik.errors.email)}
              />
            )}
            onInputChange={(event, value, reason) => {
              if (reason === "input") {
                emailOnChangeHandle({ value });
              }
            }}
            renderOption={(params, option) =>
              option.id !== 0 ? (
                <Stack
                  component="li"
                  {...params}
                  sx={{
                    alignItems: "flex-start !important",
                    padding: theme.spacing(1.4, "12px !important"),
                  }}
                >
                  <Stack direction="row" gap={1} alignItems="center">
                    <UserAvatar
                      alt=""
                      src={!_.isNil(option.avatar) ? option.avatar : undefined}
                      imgix
                      imgixWidth={24}
                      imgixHeight={24}
                      sx={{
                        width: "24px",
                        height: "24px",
                      }}
                    />
                    <Text variant="text3" fontWeight={700}>
                      {option.first_name} {option.last_name}
                    </Text>
                    <Box
                      height="22px"
                      sx={{
                        border: "1px solid",
                        borderColor: colors.blue80,
                        borderRadius: "16px",
                        padding: theme.spacing(0.1, 1.5),
                      }}
                    >
                      <Text variant="text4" color={colors.gray80}>
                        {option.email}
                      </Text>
                    </Box>
                  </Stack>
                  <Text variant="text4" color={colors.gray60} marginLeft={4}>
                    {option.company_name}
                  </Text>
                </Stack>
              ) : (
                !isEmailAssignmentEnabled && (
                  <Stack
                    component="li"
                    {...params}
                    alignItems="flex-start !important"
                    justifyContent="center"
                    gap={0.8}
                    sx={{
                      padding: theme.spacing(1, "12px !important"),
                    }}
                  >
                    <Text variant="text4" fontWeight={500}>
                      Can’t find the person you are looking for?
                    </Text>
                    <Button
                      variant="outlined"
                      color="secondary"
                      sx={{
                        display: "flex",
                        borderColor: colors.blue60,
                        gap: 0.5,
                        padding: theme.spacing(0.2, 0.8),
                        height: "24px",
                      }}
                    >
                      <SupervisorAccountIcon
                        sx={{
                          color: colors.blue60,
                          width: "16px",
                          height: "16px",
                        }}
                      />
                      <Text variant="text4" fontWeight={500}>
                        Create & assign new contact
                      </Text>
                    </Button>
                  </Stack>
                )
              )
            }
            options={userOptions}
            getOptionLabel={(option) =>
              _.isString(option) ? option : option.email
            }
            filterOptions={(options, params) => {
              let result = [...options];

              const { inputValue } = params;
              result = result.filter((option) => {
                const fullName =
                  `${option.first_name} ${option.last_name}`.toLowerCase();

                return (
                  fullName.includes(inputValue.toLowerCase()) ||
                  option.email.toLowerCase().includes(inputValue.toLowerCase())
                );
              });
              if (result.length === 0) {
                result.push({
                  id: 0,
                  first_name: "",
                  last_name: "",
                  avatar: null,
                  email: inputValue,
                  company_name: "",
                });
              }

              return result;
            }}
            onChange={userOnChangeHandle}
          />

          {!_.isNil(selectedUserOption) && (
            <Stack gap={1} marginTop={5}>
              <Text color={colors.gray60} variant="text2" fontWeight={500}>
                Contact selected to be assigned to this role:{" "}
              </Text>
              <MemoESignatureUserAssigneeBox user={selectedUserOption} />
            </Stack>
          )}

          <Stack
            direction="row"
            alignItems="center"
            alignSelf="flex-end"
            marginTop="auto"
          >
            <Button onClick={handleClose} variant="text">
              Cancel
            </Button>
            <Button
              disabled={disableAssignContactButton}
              onClick={formik.submitForm}
            >
              Assign contact
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
};

export default ESignAssigneeModificationTable;
